// hooks/usePageEngagement.ts
// ✅ v2 — Fix: sent flag prevents double-fire when both
//            visibilitychange(hidden) and beforeunload fire together (common on mobile tab close)

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { trackPageEngagement } from '@/utils/analytics';

export function usePageEngagement(pagePath: string): void {
  const startTimeRef    = useRef<number>(Date.now());
  const maxScrollPctRef = useRef<number>(0);
  // ✅ prevents double-send when both visibilitychange + beforeunload fire
  const sentRef         = useRef<boolean>(false);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    // Reset on each new page
    startTimeRef.current    = Date.now();
    maxScrollPctRef.current = 0;
    sentRef.current         = false;

    const handleScroll = () => {
      const el       = document.documentElement;
      const scrolled = el.scrollTop + window.innerHeight;
      const total    = el.scrollHeight;
      if (total <= 0) return;
      const pct = Math.round((scrolled / total) * 100);
      if (pct > maxScrollPctRef.current) maxScrollPctRef.current = pct;
    };

    const sendEngagement = () => {
      // ✅ guard: only send once per page mount
      if (sentRef.current) return;
      sentRef.current = true;

      const timeSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
      void trackPageEngagement(pagePath, maxScrollPctRef.current, timeSeconds);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') sendEngagement();
    };

    window.addEventListener('scroll',       handleScroll,      { passive: true });
    window.addEventListener('beforeunload', sendEngagement);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('scroll',       handleScroll);
      window.removeEventListener('beforeunload', sendEngagement);
      document.removeEventListener('visibilitychange', handleVisibility);
      // SPA navigation: send on unmount too
      sendEngagement();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePath]);
}